import ReceiptIcon from "@mui/icons-material/Receipt";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { Box, Button, Tooltip, Typography } from "@mui/material";
import { BaseModalComponent } from "../../common/BaseModalComponent";
import { DepositInvoiceListModalContent } from "./DepositInvoiceListModalContent";
import { getDepositTriggerButtonSx } from "./depositActionButtonStyles";

const formatAmount = (amount) => (amount != null ? `$${Number(amount).toFixed(2)}` : null);

const resolveVariant = ({ depositAmount, depositInvoice, depositInvoiceUid }) => {
  const amountLabel = formatAmount(depositAmount);

  if (!depositInvoiceUid) {
    return amountLabel
      ? { label: `${amountLabel} — Issue`, tone: "muted", tooltip: "Deposit configured but no invoice issued yet — click to create", icon: "receipt" }
      : { label: "Add deposit", tone: "muted", tooltip: "No deposit configured — click to set an amount and create the invoice", icon: "add" };
  }
  if (depositInvoice?.paidAt) return { label: amountLabel, tone: "success", tooltip: "Deposit paid — view history", icon: "receipt" };
  if (depositInvoice?.refundedAt)
    return { label: "Refunded", tone: "muted", tooltip: "Deposit refunded — click to issue a new one", icon: "receipt" };
  if (depositInvoice?.cancelledAt)
    return { label: "Cancelled", tone: "muted", tooltip: "Deposit cancelled — click to issue a new one", icon: "receipt" };
  return { label: amountLabel, tone: "pending", tooltip: "Deposit pending — view invoice", icon: "receipt" };
};

/**
 * Deposit column: same button styling as JobEventRow "Invoice" / create invoice (text + tinted bg).
 * @param {boolean} lockDepositAddActions When true (job-from-quote picker), no modal for quotes without a deposit invoice — show N/A / plain text only (paid deposits stay clickable).
 */
export function DepositCell({ quote, lockDepositAddActions = false }) {
  const { id: quoteId } = quote;
  const modalKey = `DepositInvoiceList-Quote-${quoteId}`;
  const previewKey = `DepositInvoicePreview-Quote-${quoteId}`;
  const scopeOfWorkUid = quote.scopeOfWorkUid || quote.services?.[0]?.scopeOfWork?.uid;

  const quoteTotalNumeric = Number(quote.__computed?.totalCost ?? quote.grandServiceCostCalc);
  const quoteTotal = Number.isFinite(quoteTotalNumeric) && quoteTotalNumeric > 0 ? quoteTotalNumeric : null;

  if (lockDepositAddActions && !quote.depositInvoiceUid) {
    const amountLabel = formatAmount(quote.depositAmount);
    const label = amountLabel ? `${amountLabel} — not issued` : "N/A";
    return (
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem", whiteSpace: "nowrap" }}>
          {label}
        </Typography>
      </Box>
    );
  }

  const depositPaid = Boolean(quote.depositInvoice?.paidAt);
  if (lockDepositAddActions && quote.depositInvoiceUid && depositPaid) {
    const amountLabel = formatAmount(quote.depositAmount);
    const pillSx = getDepositTriggerButtonSx("success");
    return (
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Box
          component="span"
          sx={{
            ...pillSx,
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            px: 1.5,
            py: 0.5,
            borderRadius: "8px",
            fontSize: "0.75rem",
            fontWeight: 600,
            cursor: "default",
          }}
        >
          <ReceiptIcon sx={{ fontSize: 14 }} />
          {amountLabel ?? "Paid"}
        </Box>
      </Box>
    );
  }

  const variant = resolveVariant(quote);
  const buttonSx = getDepositTriggerButtonSx(variant.tone);
  const StartIcon = variant.icon === "add" ? AddRoundedIcon : ReceiptIcon;

  return (
    <Box sx={{ display: "flex", justifyContent: "center" }}>
      <BaseModalComponent
        modalLabel={`Deposit Invoices - Quote #${quoteId}`}
        modalKey={modalKey}
        preventParentTrigger
        defaultSizes={{
          width: { xs: "95%", sm: "90%", md: "80%", lg: "720px" },
          height: { xs: "85%", sm: "75%", md: "70%", lg: "65%" },
        }}
        elementTrigger={
          <Tooltip title={variant.tooltip} arrow>
            <span>
              <Button
                variant="text"
                size="small"
                startIcon={<StartIcon sx={{ fontSize: variant.icon === "add" ? "15px !important" : "14px !important" }} />}
                sx={{ ...buttonSx, lineHeight: 1, fontSize: 11 }}
              >
                {variant.label}
              </Button>
            </span>
          </Tooltip>
        }
      >
        <DepositInvoiceListModalContent
          quoteId={quoteId}
          initialDepositAmount={quote.depositAmount ?? null}
          quoteTotal={quoteTotal}
          scopeOfWorkUid={scopeOfWorkUid}
          invoicePreviewModalKey={previewKey}
        />
      </BaseModalComponent>
    </Box>
  );
}
